import React, { FC, ReactNode } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { Article } from '../../../shared/types';
import { useParams, useHistory } from 'react-router-dom';
import { ExtendedTheme, Heading, InlineText, Loader, TextBlock, MessageView, Button } from 'neon-design-system';
import { createStyles, makeStyles } from '@material-ui/core';
import { getPublishedData } from '../../ui/PublishedBy';
import ArticleContent from '../../ui/ArticleContent';
import gql from 'graphql-tag';
import { getLinkPrefix } from '../../../utils/list-utils';
import { useActivityLogger } from '../../hooks/use-activity-logger';
import { useViewStats } from '../../hooks/use-view-stats';
import { formatDisplayDateTime } from '../../../utils/date.utils';
import { promotionalContentShortcodesToHtml } from '../../../utils/promotional-content.utils'
import Disclaimer from '../../ui/Disclaimer';
import { ArticleState } from '../../../shared/global-enums';
import { translateErrorMessage } from '../../../shared/error-handling';
import { plainText } from '../../../utils/string.utils';

interface MarketingArticleWrapperProps {
	centeredWrapper: boolean;
	children: ReactNode;
}

const useStyles = makeStyles<ExtendedTheme>(theme => createStyles({
	'@global': {
		'body': {
			backgroundColor: '#ffffff',
			color: theme.themeColors.grey[900]
		},
		/* override styles when printing */
		'#app-header': {
			display: 'none'
		},
		'@media print': {
			'body': {
				fontFamily: '"Inter", sans-serif'
			}
		}
	},
	root: {
		backgroundColor: '#ffffff',
		maxWidth: '700px',
		margin: '50px',
		'&>*': {
			marginBottom: theme.spacing(4)
		}
	},
	rootCentered : {
		backgroundColor: '#ffffff',
		maxWidth: '700px',
		margin: `${theme.spacing(8)}px auto`,
		'& > div:first-child': {
			[theme.breakpoints.down('sm')]: {
				flexDirection: 'column'
			}
		}
	},
	notificationWrapper: {
		marginTop: theme.spacing(11),
		'& h1': {
			color: theme.themeColors.grey[500]
		}
	},
	pageHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		fontSize: '11px'
	},
	marexSpectronLink: {
		color: theme.themeColors.grey[500],
		'&:visited': {
			color: theme.themeColors.grey[500]
		}
	},
	title: {
		backgroundColor: theme.themeColors.primary.main,
		marginBottom: theme.spacing(2),
		padding: theme.spacing(2)
	},
	copyText: {
		float: 'right'
	}
}));

const FIND_ARTICLE_BY_ID = gql`
	query findMarketingArticleById($id: ID!) {
		article: findMarketingArticleById(id: $id) {
			_id
			title
			html
			publishedBy { _id name type }
			tags { _id name }
			categories { _id name }
			summary
			publishDate
			lastEdited
			version
			state
			canSeeFullVersion
		}
	}
`;

const MarketingArticlePage: React.FC = () => {
	const { _id } = useParams();
	const { push } = useHistory();
	const classes = useStyles();
	const addViewStats = useViewStats();
	const archivedOrPending = (state: ArticleState) => state === ArticleState.Archived || state === ArticleState.Pending;
	const MarketingArticleWrapper: FC<MarketingArticleWrapperProps> = ({ children, centeredWrapper }) => (
		<div className={!centeredWrapper ? classes.root : classes.rootCentered}>
			<div className={classes.pageHeader}>
				<img height="40" alt="Marex" src="/insights/images/marex-dark-logo.png" />
				<a className={classes.marexSpectronLink} href="http://www.marex.com">www.marex.com</a>
			</div>
			{children}
		</div>
	);

	const { data, loading, error } = useQuery<{ article: Article }>(FIND_ARTICLE_BY_ID, {
		variables: { id: _id },
		fetchPolicy: 'network-only',
		onCompleted: (data) => {
			const article = data?.article;
			if (!article) return;

			if(!archivedOrPending(article.state)) {
				addViewStats(article);
			}
		}
	});
	const article = data?.article;
	useActivityLogger('MarketingArticle', article?.title, article?._id);

	const copyTextToClipboard = () => {
		if (navigator?.clipboard?.writeText)
		{
			navigator.clipboard.writeText(plainText(data?.article.title + ' \n' + data?.article.html));
		}
	}

	if(error) {
		return (
			<MarketingArticleWrapper centeredWrapper={!!error}>
				<div className={classes.notificationWrapper}>
					<MessageView
						styleName="primary"
						size="large"
						title={translateErrorMessage(error?.message || '')}
						primaryAction={{
							label: 'Return to Neon Insights',
							onClick: () => push('/')
						}}>
					</MessageView>
				</div> :
			</MarketingArticleWrapper>
		)
	}

	if (loading || !article) return <Loader />;

	const publishedData = getPublishedData(article, getLinkPrefix(), false, article.version > 1);

	if(!article.canSeeFullVersion) {
		return <></>;
	}

	return (
		<MarketingArticleWrapper centeredWrapper={archivedOrPending(article.state)}>
			{
				archivedOrPending(article.state) ?
					<div className={classes.notificationWrapper}>
						<MessageView
							styleName="primary"
							size="large"
							title="This article is no longer available."
							primaryAction={{
								label: 'Return to Neon Insights',
								onClick: () => push('/')
							}}>
							<TextBlock
								styleName="body"
								xs="small" sm="medium"
								color="grey"
							>
								This article was archived or unpublished by a contributor.
							</TextBlock>
						</MessageView>
					</div> :
					<>
						<div>
							<div className={classes.title} id={'article-page-title'}>
								<Heading styleName="title1" color="white">{article.title}</Heading>
							</div>
							<div id={'article-page-published-by'}>
								<InlineText styleName="caption1" color="grey" shade={500}>
									Published {formatDisplayDateTime(publishedData.date, publishedData.momentInTimeFormat)} in
								</InlineText>
								<InlineText styleName="caption1" color="grey" shade={900}>
									<strong> {article.categories.map(c => c.name).join(', ')} </strong>
								</InlineText>
								<InlineText styleName="caption1" color="grey" shade={500}>by</InlineText>
								<InlineText styleName="caption1" color="grey" shade={900}>
									<strong> {article.publishedBy.name} </strong>
								</InlineText>
								<span title="Copy text to clipboard">
									<Button className={classes.copyText} colorType="primary" onClick={copyTextToClipboard}>Export</Button>
								</span>
							</div>
						</div>
						<TextBlock color="grey" shade={400} styleName="largeBody1" xs="medium" >
							<span id={'article-page-summary'}>{article.summary}</span>
						</TextBlock>
						<ArticleContent title={article.title} html={article?.html ? promotionalContentShortcodesToHtml(article.html, []) : ''} id={article._id} isMarketing={true} />
						<Disclaimer />
					</>
			}
		</MarketingArticleWrapper>
	);
};

export default MarketingArticlePage;
